import { createFileRoute } from "@tanstack/react-router"
import {
  CalendarDays,
  CircleHelp,
  Download,
  FileSpreadsheet,
  Flag,
  Route as RouteIcon,
} from "lucide-react"

import ogImage from "@/core/assets/og.png"
import { articleJsonLd, breadcrumbJsonLd, buildHead } from "@/core/seo"

import { formatCheckedOn } from "../components/home/home-content"
import {
  AnswerBox,
  Breadcrumb,
  CtaBand,
  FaqAccordion,
  RelatedLinks,
  SimpleTable,
  SourceLine,
} from "../components/landing"
import { HeroBand, RouteSteps, SectionTitle } from "../components/landing/page-parts"
import { IconTile, LogbookIllustration } from "../components/landing/product-art"
import { ATO_LOGBOOK } from "../content/pages/faq-items"

/** Own dates: update only when the page is reviewed against the ATO page. */
export const REVIEWED_ISO = "2026-10-28"
const TEMPLATE_CHECKED = formatCheckedOn(REVIEWED_ISO)

/** Design: docs/design/html/landing/web-landing-template.html. Files: scripts/build-logbook-template.mjs. */
export const logbookTemplateSeo = () =>
  buildHead({
    // Title and description: docs/design/landing/copy-products.md.
    title: "Free ATO logbook template (CSV and PDF)",
    description:
      "Download a free vehicle logbook template as CSV for Excel or Google Sheets, or as a PDF to print. Includes an example and what to record. No sign-up.",
    path: "/ato-logbook-template",
    jsonLd: [
      articleJsonLd({
        headline: "Free ATO logbook template for your car (CSV and PDF)",
        path: "/ato-logbook-template",
        image: ogImage,
        datePublished: REVIEWED_ISO,
        dateModified: REVIEWED_ISO,
      }),
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Logbook template" }]),
    ],
  })

export const Route = createFileRoute("/_brand-layout/ato-logbook-template")({
  head: () => {
    const { meta, links, scripts } = logbookTemplateSeo()
    return { meta, links, scripts }
  },
  component: LogbookTemplatePage,
})

/** Must match COLUMNS in scripts/build-logbook-template.mjs. */
export const COLUMNS = ["Date", "Start km", "End km", "Km", "Purpose", "Use"]

/** Fictional trips; no real places. */
export const EXAMPLE_ROWS = [
  ["Mon 14 Sep", "46,980", "46,998", "18", "Quote at client site", "Business"],
  ["Tue 15 Sep", "46,998", "47,033", "35", "Parts pickup from supplier", "Business"],
  ["Tue 15 Sep", "47,033", "47,041", "8", "Home", "Private"],
]

const STEPS = [
  {
    title: "Before day one",
    body: "Write the start date and the odometer reading.",
    icon: CalendarDays,
  },
  {
    title: "Every business trip",
    body: "Date, start and end odometer, kilometres, and a clear purpose.",
    icon: RouteIcon,
  },
  {
    title: "Last day",
    body: "Write the end date and the odometer reading, and work out the business-use percentage.",
    icon: Flag,
  },
]

const FAQ = [
  {
    question: "Is a spreadsheet logbook accepted?",
    answer:
      "The ATO asks that you keep the required details for the period. It does not tell you which tool to use.",
  },
  {
    question: "Can I fill it in afterwards?",
    answer: "Fill it in as you go. A logbook rebuilt from memory is harder to rely on.",
  },
]

const downloadClass =
  "bg-primary text-primary-foreground focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md px-5 font-semibold outline-none focus-visible:ring-2"

export function LogbookTemplatePage() {
  return (
    <>
      <Breadcrumb
        items={[{ label: "Home", href: "/" }, { label: "Guides" }, { label: "Logbook template" }]}
      />
      <HeroBand>
        <p className="text-muted-foreground text-sm">
          Free template · not an ATO form · Last reviewed {TEMPLATE_CHECKED}
        </p>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          Free ATO logbook template for your car (CSV and PDF)
        </h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={<SourceLine sources={[ATO_LOGBOOK]} checked={TEMPLATE_CHECKED} />}>
            Download a CSV for Excel or Google Sheets, or a PDF to print. The CSV has a column for
            the date, start and end odometer, kilometres, purpose and business or private use of
            each trip, and the PDF adds boxes for the start and end of your 12 weeks. The ATO page
            on the logbook method lists what you must record.
          </AnswerBox>
        </div>
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="download-title">
        <SectionTitle id="download-title" icon={Download} tone="primary">
          Download the template
        </SectionTitle>
        <div className="flex flex-wrap gap-3">
          <a href="/templates/kestly-logbook-template.csv" download className={downloadClass}>
            CSV (Excel, Google Sheets)
          </a>
          <a href="/templates/kestly-logbook-template.pdf" download className={downloadClass}>
            PDF to print
          </a>
        </div>
        <div className="border-border bg-card grid max-w-[70ch] gap-3 rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <IconTile icon={FileSpreadsheet} tone="success" />
            <div>
              <b className="block">Columns for each trip</b>
              <span className="text-muted-foreground text-sm">First row of the CSV</span>
            </div>
          </div>
          <ol aria-label="CSV columns" className="flex flex-wrap gap-2 font-mono text-sm">
            {COLUMNS.map((c) => (
              <li key={c} className="bg-muted text-foreground rounded-md px-2.5 py-1">
                {c}
              </li>
            ))}
          </ol>
        </div>
        <figure className="grid max-w-[420px] gap-2">
          <LogbookIllustration />
          <figcaption className="text-muted-foreground text-center text-sm">
            Example: a 12-week logbook, filled week by week
          </figcaption>
        </figure>
        <p className="text-muted-foreground max-w-[70ch]">
          No sign-up. This template is not issued or endorsed by the ATO. It is a layout only; the
          ATO&apos;s rules decide what you must record.
        </p>
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="example-title">
        <SectionTitle id="example-title" icon={FileSpreadsheet} tone="success">
          Logbook example
        </SectionTitle>
        <p className="max-w-[70ch]">
          Three example rows from a vehicle logbook. Your logbook is a record you keep; use your own
          trips.
        </p>
        <SimpleTable
          caption="Example rows from a vehicle logbook (fictional trips)"
          columns={COLUMNS}
          rows={EXAMPLE_ROWS.map(([header, ...cells]) => ({ header: header!, cells }))}
        />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="steps-title">
        <SectionTitle id="steps-title" icon={RouteIcon} tone="warning">
          How to fill it in
        </SectionTitle>
        <RouteSteps steps={STEPS} />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="faq-title">
        <SectionTitle id="faq-title" icon={CircleHelp} tone="success">
          Common questions
        </SectionTitle>
        <FaqAccordion items={FAQ} />
        <SourceLine sources={[ATO_LOGBOOK]} checked={TEMPLATE_CHECKED} />
      </section>

      <RelatedLinks
        links={[
          { label: "Mileage tracker: automatic GPS logbook", href: "/mileage-tracker" },
          {
            label: "ATO logbook requirements: what to record",
            href: "/guides/ato-logbook-requirements",
          },
          { label: "Logbook method: when it pays off", href: "/logbook-method" },
          { label: "Cents per km rate and calculator", href: "/cents-per-km-calculator" },
        ]}
      />

      <CtaBand
        title="Let your phone do the typing"
        description="GPS trips, a reason for each, and a CSV trip log when the 12 weeks are up."
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
